import { useEffect, useRef, useState } from 'react';
import {
  useCurrentUserApi,
  useMemberApi,
  useMemberCreateApi,
  useMemberUpdateApi,
} from '@hero/api-hooks';
import { Member, getObjectId, Id } from '@hero/schema';
import { useMemberForm, MemberFormValues } from './useMemberForm';

export interface UseMemberCreateAdapterOptions {
  onMemberSaved?: (member: Member) => void;
  onError?: (error: Error) => void;
  /** When set, load member for edit; when undefined, create mode. */
  memberId?: Id;
  /** Optional initial form values (e.g. pre-fill type). Overwritten when loading an item via memberId. */
  defaultValues?: Partial<MemberFormValues>;
}

export const useMemberCreateAdapter = (options: UseMemberCreateAdapterOptions) => {
  const { memberId, onMemberSaved, onError } = options;
  const isEdit = memberId != null;
  const memberQuery = useMemberApi(memberId);
  const savedMember = memberQuery.data;
  const createMember = useMemberCreateApi();
  const updateMember = useMemberUpdateApi();
  const { data: me } = useCurrentUserApi();
  const myPhone = me?.user?.phone;
  const isSelf = isEdit && Boolean(myPhone) && savedMember?.phone === myPhone;
  /**
   * On a number change, keep the old number's login on the company.
   * Unchecked by default: a mistyped number leaves the company. The
   * backend always keeps you when the old number is yours.
   */
  const [keepOldUserOnOrg, setKeepOldUserOnOrg] = useState(false);
  /**
   * Also rename the driver's account another org uses. Unchecked by
   * default: the other org knows him by the account's name.
   */
  const [renameAccount, setRenameAccount] = useState(false);

  async function onSubmit(values: MemberFormValues) {
    try {
      if (isEdit && memberId) {
        const phoneChanged = values.phone !== savedMember?.phone;
        const result = await updateMember.mutateAsync({
          _id: getObjectId(memberId),
          type: values.type,
          name: values.name,
          phone: values.phone,
          ...(phoneChanged ? { keepOldUserOnOrg } : {}),
          ...(savedMember?.sharedAccount ? { renameAccount } : {}),
          email: values.email || undefined,
          driver: values.driver,
        });
        onMemberSaved?.(result);
      } else {
        const result = await createMember.mutateAsync(values);
        onMemberSaved?.(result);
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
    }
  }

  const formHook = useMemberForm({
    defaultValues: options.defaultValues,
    onSubmit,
    isEdit,
  });

  const formInitializedRef = useRef(false);
  useEffect(() => {
    if (isEdit && savedMember && !formInitializedRef.current) {
      formInitializedRef.current = true;
      formHook.setMember(savedMember);
    }
    if (!isEdit) {
      formInitializedRef.current = false;
    }
  }, [isEdit, savedMember, formHook.setMember]);

  const watchedPhone = formHook.watch('phone');
  const phoneChanging =
    isEdit && Boolean(savedMember?.phone) && Boolean(watchedPhone) && watchedPhone !== savedMember?.phone;
  const watchedName = formHook.watch('name');
  const sharedAccount = savedMember?.sharedAccount;
  const accountNameDiffers =
    Boolean(sharedAccount) && Boolean(watchedName) && watchedName !== sharedAccount?.name;

  const reset = () => {
    createMember.reset();
    updateMember.reset();
    setKeepOldUserOnOrg(false);
    setRenameAccount(false);
    if (savedMember) {
      formHook.setMember(savedMember);
    } else {
      formHook.reset();
    }
  };

  const onSaveClick = () => {
    formHook.onValidSubmit();
  };

  return {
    memberQuery,
    createMember,
    updateMember,
    formHook,
    reset,
    onSaveClick,
    savedMember,
    isEdit,
    /**
     * Your own number is your sign-in, so it is not edited here. Anyone
     * else's can be: the company's row moves to the login for the new
     * number and the old login keeps its phone (`changeUserPhone`).
     */
    isPhoneLocked: isSelf,
    /** Set while an existing member's number is being changed. */
    phoneChange: phoneChanging
      ? { oldPhone: savedMember?.phone as string, keepOldUserOnOrg, setKeepOldUserOnOrg }
      : undefined,
    /**
     * Set while a driver whose account another org also uses gets a name
     * other than the account's. An account only this org uses follows
     * on its own.
     */
    accountRename:
      accountNameDiffers && sharedAccount
        ? { accountName: sharedAccount.name, renameAccount, setRenameAccount }
        : undefined,
  };
};
